import { useCallback, useEffect, useRef } from 'react';
import type { Direction, Position, Collectible } from '@/types/game';
import type { EnemyState } from '@/types/enemy';
import type { EnemyWorkerRequest, EnemyWorkerResponse } from '@/workers/enemy.worker';
import {
  slideInDirection,
  moveAllEnemiesSync,
  moveAllEnemiesMultiStep,
} from '@/game/enemyAI';

// Re-export slideInDirection so existing consumers keep working
export { slideInDirection } from '@/game/enemyAI';

// ---------------------------------------------------------------------------
// Main hook
// ---------------------------------------------------------------------------

export function useEnemyEngine() {
  const workerRef = useRef<Worker | null>(null);
  const pendingRef = useRef<Map<number, { resolve: (v: EnemyState[]) => void }>>(new Map());
  const requestIdRef = useRef(0);

  // Initialize worker
  useEffect(() => {
    try {
      const worker = new Worker(
        new URL('../workers/enemy.worker.ts', import.meta.url),
        { type: 'module' }
      );
      worker.onmessage = (event: MessageEvent<EnemyWorkerResponse>) => {
        const { id, payload } = event.data;
        const pending = pendingRef.current.get(id);
        if (pending) {
          pending.resolve(payload);
          pendingRef.current.delete(id);
        }
      };
      worker.onerror = () => {
        // Worker failed, fall back to sync
        workerRef.current = null;
      };
      workerRef.current = worker;
    } catch {
      // Worker creation failed, will use sync fallback
      workerRef.current = null;
    }

    return () => {
      workerRef.current?.terminate();
      workerRef.current = null;
    };
  }, []);

  /**
   * Moves all enemies via the web worker (async). Falls back to sync on main thread.
   */
  const moveAllEnemiesAsync = useCallback(
    (
      enemies: EnemyState[],
      grid: string[],
      gridWidth: number,
      playerPosition: Position,
      playerPrevPosition: Position,
      playerLastDirection: Direction | null,
      collectibles: Collectible[] = [],
    ): Promise<EnemyState[]> => {
      const worker = workerRef.current;
      if (worker) {
        return new Promise<EnemyState[]>((resolve) => {
          const id = requestIdRef.current++;
          pendingRef.current.set(id, { resolve });

          const message: EnemyWorkerRequest = {
            type: 'MOVE_ALL_ENEMIES',
            id,
            payload: {
              enemies,
              grid,
              gridWidth,
              playerPosition,
              playerPrevPosition,
              playerLastDirection,
              collectibles,
            },
          };
          worker.postMessage(message);

          // Timeout fallback: if worker does not respond in 500ms, compute synchronously
          setTimeout(() => {
            if (pendingRef.current.has(id)) {
              pendingRef.current.delete(id);
              resolve(moveAllEnemiesMultiStep(enemies, grid, gridWidth, playerPosition, playerPrevPosition, playerLastDirection, collectibles));
            }
          }, 500);
        });
      }
      // Sync fallback (multi-step handles movesPerTurn > 1)
      return Promise.resolve(
        moveAllEnemiesMultiStep(enemies, grid, gridWidth, playerPosition, playerPrevPosition, playerLastDirection, collectibles)
      );
    },
    [],
  );

  /**
   * Synchronous enemy movement (main thread fallback + used by existing callers).
   */
  const moveAllEnemies = useCallback(
    (
      enemies: EnemyState[],
      grid: string[],
      gridWidth: number,
      playerPosition: Position,
      playerPrevPosition: Position,
      playerLastDirection: Direction | null,
      collectibles: Collectible[] = [],
    ): EnemyState[] => {
      return moveAllEnemiesMultiStep(enemies, grid, gridWidth, playerPosition, playerPrevPosition, playerLastDirection, collectibles);
    },
    [],
  );

  /**
   * Checks whether the player occupies the same tile as any enemy.
   * Returns the colliding enemy or null.
   */
  const checkCollision = useCallback(
    (playerPos: Position, enemies: EnemyState[]): EnemyState | null => {
      return (
        enemies.find(
          (e) => e.position[0] === playerPos[0] && e.position[1] === playerPos[1],
        ) ?? null
      );
    },
    [],
  );

  return { moveAllEnemies, moveAllEnemiesAsync, checkCollision, slideInDirection };
}
